// Header "Authorization: Bearer <token>" para que la API pueda verificar criptograficamente
// quien esta llamando (ver Auth0JwtMiddleware.cs en api.hawk) -- hasta ahora la API confiaba
// a ciegas en el header "user" que arma este mismo dataProvider mas abajo, que cualquiera
// podia falsificar con curl/Postman sin pasar por Auth0 en absoluto.
//
// Compartido entre App.jsx, withValidations.jsx y PrintRequestShow.jsx (los lugares que hoy
// arman sus propios headers de autenticacion) para no repetir esta logica in situ cuatro
// veces. Recibe getAccessTokenSilently ya resuelto por el useAuth0() de quien llama (no se
// puede usar el hook aca adentro, esto no es un componente ni un hook propio).
//
// Mientras el backend este en rollout gradual (Auth0JwtEnforce="false" en
// local.settings.json) o mientras REACT_APP_AUTH0_AUDIENCE todavia no este configurado (falta
// crear la API en el dashboard de Auth0), un fallo aca NO debe romper ninguna pantalla: se
// loguea en consola y se sigue sin el header, tal como el backend sigue sin bloquear nada
// mientras esta en modo "solo observar".
//
// El audience se pide EXPLICITAMENTE aca (y no en el authorizationParams del Auth0Provider en
// index.jsx) para que solo afecte a este pedido puntual de Access Token, y nunca al login
// inicial -- si el client todavia no esta autorizado para la API en el dashboard de Auth0
// (Applications > APIs > tu API > Application Access), esto falla y cae al catch de abajo en
// vez de romper el login completo.
//
// loginWithRedirect (opcional, tercer parametro) es SOLO para withValidations.jsx, el unico
// lugar que corre una vez al arrancar la app, antes de mostrar ninguna pantalla real -- ver el
// comentario de CONSENT_REDIRECT_GUARD_KEY mas abajo. App.jsx y PrintRequestShow.jsx llaman a
// esta funcion sin ese tercer parametro (o sea, nunca redirigen), porque ahi el usuario puede
// estar en medio de llenar un formulario y una redireccion inesperada le haria perder lo que
// esta haciendo.
import States from './States';

const CONSENT_REDIRECT_GUARD_KEY = 'hawk_auth0_consent_redirect_attempted';

const buildAuthorizationHeader = async (getAccessTokenSilently, loginWithRedirect) => {
    // PRUEBA TEMPORAL de Auth0 Organizations (ver conversacion): si ya se pidio un Access
    // Token scoped a la Empresa activa (ver getAccessTokenWithPopup en CompaniesShow.jsx,
    // que lo guarda con States.setCompanyToken al entrar a su pantalla), lo usamos directo
    // en vez de pedir el token generico de audience solo -- asi TODOS los llamados de ahi en
    // mas (App.jsx/fetchJson, delete, auth, y este mismo helper desde donde sea que se
    // llame) viajan con los "permissions" de esa organizacion, y se puede probar que el
    // backend los exige de verdad (ver RegisterInvoice en Providersapi.cs). El org_id de esa
    // Empresa ya no esta quemado a Vecame -- se resuelve por Empresa desde
    // empresas.ID_Organizacion_AUTH0 (ver CompanyAuth.js/resolveCompanyOrgId); sacar este
    // bloque cuando el scoping por resource/pantalla este resuelto de verdad (Fase 4, paso
    // pendiente).
    const companyToken = States.getCompanyToken();
    if (companyToken) {
        return { Authorization: `Bearer ${companyToken}` };
    }

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE ?? "";
    try {
        const token = await getAccessTokenSilently(
            audience ? { authorizationParams: { audience } } : undefined
        );
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (e) {
        console.warn('No se pudo obtener el Access Token de Auth0 (revisa REACT_APP_AUTH0_AUDIENCE en .env y que la API exista en el dashboard de Auth0).', e);

        // "consent_required" (o a veces "login_required") en un pedido SILENCIOSO
        // (prompt=none) puede pasar aunque la app ya este autorizada y marcada First Party en
        // el dashboard de Auth0 -- sobre todo probando contra http://localhost, donde Auth0 no
        // siempre puede confiar automaticamente en el dominio para saltear el consentimiento.
        // Una vez que el usuario lo aprueba de forma interactiva UNA vez, Auth0 lo recuerda
        // para siempre y los pedidos silenciosos futuros funcionan solos -- por eso, si nos
        // pasaron loginWithRedirect (solo pasa desde withValidations.jsx, al arrancar la app),
        // reintentamos con una redireccion normal a Auth0 (la misma pantalla de "Autorizar" de
        // siempre). El flag en sessionStorage asegura que esto se intente como maximo una vez
        // por sesion de navegador: si sigue fallando despues de la redireccion (por el motivo
        // que sea), no queremos volver a redirigir en loop -- simplemente se sigue sin el
        // header, como ya haciamos antes.
        const isConsentIssue = e?.error === 'consent_required' || e?.error === 'login_required';
        if (isConsentIssue && loginWithRedirect && audience) {
            try {
                if (!sessionStorage.getItem(CONSENT_REDIRECT_GUARD_KEY)) {
                    sessionStorage.setItem(CONSENT_REDIRECT_GUARD_KEY, '1');
                    await loginWithRedirect({ authorizationParams: { audience } });
                }
            } catch (e2) {
                console.warn('No se pudo redirigir a autorizar el acceso a la API.', e2);
            }
        }

        return {};
    }
};

export { buildAuthorizationHeader };
